/**
 * Agent-Chat File Manager Page
 * Uses @agion/ui design system components
 */

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Download,
  Trash2,
  FileText,
  Table,
  Code,
  File,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import {
  Card,
  Badge,
  Button,
  Input,
  SearchInput,
  Spinner,
  EmptyState,
  cn,
} from '@agion/ui'
import { useFileStore } from '../hooks/useFiles'
// F9 (chat-ui catch-up): the file-chat input + direct-agent invocation
// surface was removed. Files are now consumed by attaching them to a
// mission run via the Missions page.
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import type { UploadedFile, FileStatus } from '../types/files'

// Status badge variants
const statusVariant: Record<FileStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  uploading: 'info',
  processing: 'warning',
  completed: 'success',
  error: 'error',
}

// File type configuration
const fileTypeConfig: Record<string, { icon: typeof File; color: string; bgColor: string }> = {
  excel: { icon: Table, color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-900/30' },
  csv: { icon: Table, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-900/30' },
  json: { icon: Code, color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-900/30' },
  parquet: { icon: Table, color: 'text-purple-600', bgColor: 'bg-purple-50 dark:bg-purple-900/30' },
  pdf: { icon: FileText, color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-900/30' },
  default: { icon: File, color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-50 dark:bg-slate-800' },
}

// Get file type from filename
const getFileType = (file: UploadedFile): string => {
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (ext === 'xlsx' || ext === 'xls') return 'excel'
  if (ext === 'csv') return 'csv'
  if (ext === 'json') return 'json'
  if (ext === 'parquet') return 'parquet'
  if (ext === 'pdf') return 'pdf'
  return 'default'
}

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

// Format date relative - uses i18n
const formatDate = (date: Date | undefined, t: (key: string, options?: Record<string, unknown>) => string, lng?: string): string => {
  if (!date) return '—'

  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor(diff / (1000 * 60))

  if (minutes < 1) return t('pages:files.time.justNow', { defaultValue: 'Just now' })
  if (minutes < 60) return t('pages:files.time.minutesAgo', { count: minutes, defaultValue: '{{count}}m ago' })
  if (hours < 24) return t('pages:files.time.hoursAgo', { count: hours, defaultValue: '{{count}}h ago' })
  if (days < 7) return t('pages:files.time.daysAgo', { count: days, defaultValue: '{{count}}d ago' })

  // Use locale-aware date formatting
  const locale = lng === 'fi' ? 'fi-FI' : lng === 'sv' ? 'sv-SE' : 'en-US'
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(date)
}

type SortField = 'name' | 'size' | 'uploadedAt' | 'status'
type SortDirection = 'asc' | 'desc'

// File Row Component
const FileRow: React.FC<{
  file: UploadedFile
  onDownload: () => void
  onDelete: () => void
  onRename: (newName: string) => void
}> = ({ file, onDownload, onDelete, onRename }) => {
  const { t, i18n } = useTranslation(['pages', 'common'])
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(file.name)

  const fileType = getFileType(file)
  const typeConfig = fileTypeConfig[fileType] ?? fileTypeConfig['default']!
  const IconComponent = typeConfig.icon

  const handleFinishRename = () => {
    if (editName.trim() && editName !== file.name) {
      onRename(editName.trim())
    }
    setIsEditing(false)
  }

  return (
    <motion.tr
      className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      {/* Name Column */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          {/* File Type Icon */}
          <div className={cn('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', typeConfig.bgColor)}>
            <IconComponent className={cn('w-4 h-4', typeConfig.color)} />
          </div>

          {/* File Name */}
          {isEditing ? (
            <Input
              type="text"
              value={editName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditName(e.target.value)}
              onBlur={handleFinishRename}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') handleFinishRename()
                if (e.key === 'Escape') {
                  setEditName(file.name)
                  setIsEditing(false)
                }
              }}
              className="flex-1 h-8"
            />
          ) : (
            <span
              className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate cursor-pointer hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
              onClick={() => setIsEditing(true)}
              title={t('pages:files.clickToRename')}
            >
              {file.name}
            </span>
          )}
        </div>
      </td>

      {/* Size Column */}
      <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {formatFileSize(file.size)}
      </td>

      {/* Date Column */}
      <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {formatDate(file.uploadedAt, t, i18n.language)}
      </td>

      {/* Status Column */}
      <td className="py-3 px-4">
        <Badge variant={statusVariant[file.status]} size="sm">
          {file.status === 'processing' && (
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse mr-1.5" />
          )}
          {t(`common:status.${file.status}`)}
        </Badge>
      </td>

      {/* Actions Column */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            onClick={onDownload}
            title={t('common:download')}
            className="h-8 w-8 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
          >
            <Download className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            title={t('common:delete')}
            className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </motion.tr>
  )
}

// Sort Header Component
const SortHeader: React.FC<{
  label: string
  field: SortField
  currentSort: SortField
  direction: SortDirection
  onSort: (field: SortField) => void
  className?: string
}> = ({ label, field, currentSort, direction, onSort, className = '' }) => {
  const isActive = currentSort === field

  return (
    <th
      className={cn(
        'py-3 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors select-none',
        className
      )}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive && (
          direction === 'asc'
            ? <ChevronUp className="w-3 h-3" />
            : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  )
}

export const FileManagerPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'common'])
  const { files, removeFile, renameFile, loadFiles, isLoading } = useFileStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<SortField>('uploadedAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Load files when component mounts
  useEffect(() => {
    loadFiles().catch(error => {
      console.error('[FileManagerPage] Failed to load files:', error)
      toast.error(t('pages:files.loadError', 'Failed to load files'))
    })
  }, [loadFiles, t])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleDeleteFile = async (fileId: string) => {
    if (!window.confirm(t('pages:files.deleteConfirm'))) {
      return
    }

    try {
      await removeFile(fileId)
      toast.success(t('pages:files.deleteSuccess'))
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        toast.success(t('pages:files.fileRemoved'))
        await loadFiles()
      } else {
        toast.error(t('pages:files.deleteError'))
        console.error('Error deleting file:', error)
      }
    }
  }

  const handleDownloadFile = async (fileId: string, filename: string) => {
    try {
      // Step 1: Get file metadata to obtain blob_path
      const metadataResponse = await fetch(`/v1/files/${fileId}`, {
        credentials: 'include',
      })
      if (!metadataResponse.ok) throw new Error('Failed to get file metadata')

      const fileData = await metadataResponse.json()
      const blobPath = fileData.blob_path || fileData.blobPath

      if (!blobPath) {
        throw new Error('File blob path not found')
      }

      // Step 2: Download from storage using blob_path
      const downloadResponse = await fetch(`/v1/governance/storage/download/uploads/${blobPath}`, {
        credentials: 'include',
      })
      if (!downloadResponse.ok) throw new Error('Failed to download file')

      const blob = await downloadResponse.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success(t('pages:files.downloadSuccess'))
    } catch (error) {
      toast.error(t('pages:files.downloadError'))
      console.error('Error downloading file:', error)
    }
  }

  const handleRenameFile = (fileId: string, newName: string) => {
    renameFile(fileId, newName)
  }

  const handleSearchClear = () => {
    setSearchTerm('')
  }

  // Filter and sort files
  const filteredFiles = files
    .filter(file =>
      file.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'size':
          comparison = a.size - b.size
          break
        case 'uploadedAt':
          comparison = (a.uploadedAt?.getTime() || 0) - (b.uploadedAt?.getTime() || 0)
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950">
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('pages:files.title')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {t('pages:files.subtitle', 'Manage your uploaded files and data')}
            </p>
          </div>

          {/* Search */}
          <div className="mb-6">
            <SearchInput
              placeholder={t('pages:files.searchPlaceholder')}
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onClear={handleSearchClear}
              size="md"
            />
          </div>

          {/* Files Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                <Spinner size="md" />
                <span>{t('pages:files.loading')}</span>
              </div>
            </div>
          ) : filteredFiles.length === 0 ? (
            <EmptyState
              icon={FileText}
              title={searchTerm ? t('pages:files.noFilesFound') : t('pages:files.noFilesUploaded')}
              description={searchTerm ? t('pages:files.adjustSearch') : t('pages:files.uploadFirst')}
              size="md"
            />
          ) : (
            <>
              {/* Results count */}
              <div className="mb-4 text-sm text-slate-500 dark:text-slate-400">
                {t('pages:files.count', { count: filteredFiles.length })}
              </div>

              {/* Table */}
              <Card className="overflow-hidden">
                {/* eslint-disable-next-line no-restricted-syntax -- Complex file table with custom sort headers; DataTable migration tracked separately */}
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <SortHeader
                        label={t('pages:files.columnName', 'Name')}
                        field="name"
                        currentSort={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="w-[45%]"
                      />
                      <SortHeader
                        label={t('pages:files.columnSize', 'Size')}
                        field="size"
                        currentSort={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="w-[15%]"
                      />
                      <SortHeader
                        label={t('pages:files.columnDate', 'Uploaded')}
                        field="uploadedAt"
                        currentSort={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="w-[15%]"
                      />
                      <SortHeader
                        label={t('pages:files.columnStatus', 'Status')}
                        field="status"
                        currentSort={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        className="w-[15%]"
                      />
                      <th className="py-3 px-4 w-[10%]" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredFiles.map((file) => (
                      <FileRow
                        key={file.id}
                        file={file}
                        onDownload={() => handleDownloadFile(file.id, file.name)}
                        onDelete={() => handleDeleteFile(file.id)}
                        onRename={(newName) => handleRenameFile(file.id, newName)}
                      />
                    ))}
                  </tbody>
                </table>
              </Card>

              {/* Hint */}
              <div className="mt-6 text-center text-sm text-slate-400 dark:text-slate-500">
                {t('pages:files.referenceHint')}
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  )
}

export default FileManagerPage
